import React, { useState, useEffect } from 'react';
import {
  Plus, Wallet, TrendingUp, TrendingDown, Download, Trash2,
  Edit2, Filter, X, CheckCircle, Clock, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../utils/api';
import { formatDateBR } from '../utils/formatDate';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

// ─── helpers ──────────────────────────────────────────────────
const fmtBRL  = v => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = d => formatDateBR(d);
const hoje    = () => new Date().toISOString().slice(0, 10);
const primeiroDiaMes = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

const FORMAS_PAG  = ['dinheiro', 'pix', 'cartao_debito', 'cartao_credito', 'transferencia', 'boleto', 'outro'];
const TIPOS_CONTA = ['caixa', 'banco', 'pix', 'cartao', 'outro'];

const LANC_VAZIO = {
  data_movimento: hoje(), tipo: 'saida', status: 'realizado', conta_id: '', categoria_id: '',
  valor: '', descricao: '', forma_pagamento: 'pix', observacao: '', animal_id: '', lote_id: '', produto_id: '',
};
const CAT_VAZIA  = { nome: '', tipo: 'saida', grupo: '', ativo: true };
const CONTA_VAZIA = { nome: '', tipo: 'caixa', saldo_inicial: '0', data_saldo_inicial: hoje(), ativo: true };

// ─── componente ───────────────────────────────────────────────
export default function FluxoCaixa() {
  const [tab, setTab] = useState('visao_geral');

  // dados
  const [resumo, setResumo]       = useState(null);
  const [graficos, setGraficos]   = useState(null);
  const [lancamentos, setLancs]   = useState([]);
  const [totalLanc, setTotalLanc] = useState(0);
  const [totalPags, setTotalPags] = useState(1);
  const [todasCats, setTodasCats] = useState([]);
  const [contas, setContas]       = useState([]);
  const [animais, setAnimais]     = useState([]);
  const [lotes, setLotes]         = useState([]);
  const [produtos, setProdutos]   = useState([]);
  const [gruposSaida, setGruposSaida]     = useState([]);
  const [gruposEntrada, setGruposEntrada] = useState([]);

  const [loading, setLoading]     = useState(true);
  const [loadingL, setLoadingL]   = useState(false);

  // filtros
  const [filtros, setFiltros] = useState({
    data_inicio: primeiroDiaMes(), data_fim: hoje(),
    tipo: '', status: '', conta_id: '', categoria_id: '', busca: '',
  });
  const [pagina, setPagina] = useState(1);

  // modais
  const [showLanc, setShowLanc]   = useState(false);
  const [editLanc, setEditLanc]   = useState(null);
  const [formLanc, setFormLanc]   = useState(LANC_VAZIO);

  const [showCat, setShowCat]     = useState(false);
  const [editCat, setEditCat]     = useState(null);
  const [formCat, setFormCat]     = useState(CAT_VAZIA);

  const [showConta, setShowConta] = useState(false);
  const [editConta, setEditConta] = useState(null);
  const [formConta, setFormConta] = useState(CONTA_VAZIA);

  // setters parciais
  const fltF  = (k, v) => setFiltros(p => ({ ...p, [k]: v }));
  const lancF = (k, v) => setFormLanc(p => ({ ...p, [k]: v }));
  const catF  = (k, v) => setFormCat(p => ({ ...p, [k]: v }));
  const ctaF  = (k, v) => setFormConta(p => ({ ...p, [k]: v }));

  // ── carga inicial ──
  useEffect(() => {
    Promise.all([
      api.get('/fluxo-caixa/contas').then(r => setContas(r.data)).catch(() => {}),
      api.get('/fluxo-caixa/categorias/todas').then(r => setTodasCats(r.data)).catch(() => {}),
      api.get('/animais', { params: { situacao: 'ativo', limit: 500 } })
        .then(r => setAnimais(r.data.data || r.data || [])).catch(() => {}),
      api.get('/lotes').then(r => setLotes(r.data || [])).catch(() => {}),
      api.get('/estoque/produtos').then(r => setProdutos(r.data || [])).catch(() => {}),
      api.get('/fluxo-caixa/grupos', { params: { tipo: 'saida' } }).then(r => setGruposSaida(r.data || [])).catch(() => {}),
      api.get('/fluxo-caixa/grupos', { params: { tipo: 'entrada' } }).then(r => setGruposEntrada(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const reloadContas = () => api.get('/fluxo-caixa/contas').then(r => setContas(r.data)).catch(() => {});
  const reloadCats   = () => api.get('/fluxo-caixa/categorias/todas').then(r => setTodasCats(r.data)).catch(() => {});

  // ── resumo + gráficos ao mudar período/conta ──
  useEffect(() => {
    const p = { data_inicio: filtros.data_inicio, data_fim: filtros.data_fim };
    if (filtros.conta_id) p.conta_id = filtros.conta_id;
    api.get('/fluxo-caixa/resumo', { params: p }).then(r => setResumo(r.data)).catch(() => {});
    api.get('/fluxo-caixa/graficos', { params: p }).then(r => setGraficos(r.data)).catch(() => {});
  }, [filtros.data_inicio, filtros.data_fim, filtros.conta_id]);

  // ── lançamentos ao mudar filtros/página ──
  useEffect(() => {
    setLoadingL(true);
    const p = { ...filtros, page: pagina, limit: 50 };
    Object.keys(p).forEach(k => !p[k] && delete p[k]);
    api.get('/fluxo-caixa/lancamentos', { params: p })
      .then(r => { setLancs(r.data.data || []); setTotalLanc(r.data.total || 0); setTotalPags(r.data.pages || 1); })
      .catch(() => {})
      .finally(() => setLoadingL(false));
  }, [filtros, pagina]);

  const reloadLancamentos = () => {
    const p = { ...filtros, page: pagina, limit: 50 };
    Object.keys(p).forEach(k => !p[k] && delete p[k]);
    api.get('/fluxo-caixa/lancamentos', { params: p })
      .then(r => { setLancs(r.data.data || []); setTotalLanc(r.data.total || 0); setTotalPags(r.data.pages || 1); })
      .catch(() => {});
    const rp = { data_inicio: filtros.data_inicio, data_fim: filtros.data_fim };
    if (filtros.conta_id) rp.conta_id = filtros.conta_id;
    api.get('/fluxo-caixa/resumo', { params: rp }).then(r => setResumo(r.data)).catch(() => {});
    api.get('/fluxo-caixa/graficos', { params: rp }).then(r => setGraficos(r.data)).catch(() => {});
  };

  // categorias filtradas por tipo do lançamento para o modal
  const catsFiltradas = todasCats.filter(c => c.ativo && (c.tipo === formLanc.tipo || c.tipo === 'ambos'));

  // grupos gerenciais disponíveis pro tipo escolhido na categoria (+ valor atual, se for um grupo antigo fora da lista fixa)
  const gruposDisponiveis = (() => {
    const base = formCat.tipo === 'entrada' ? gruposEntrada : formCat.tipo === 'saida' ? gruposSaida : [...gruposSaida, ...gruposEntrada];
    return formCat.grupo && !base.includes(formCat.grupo) ? [formCat.grupo, ...base] : base;
  })();

  // ── CRUD Lançamentos ──
  const abrirNovoLanc = () => {
    setEditLanc(null);
    setFormLanc({ ...LANC_VAZIO, conta_id: contas.find(c => c.ativo)?.id?.toString() || '' });
    setShowLanc(true);
  };
  const abrirEditLanc = l => {
    setEditLanc(l);
    setFormLanc({
      data_movimento: l.data_movimento?.slice(0, 10) || hoje(),
      tipo: l.tipo, status: l.status,
      conta_id: String(l.conta_id), categoria_id: String(l.categoria_id),
      valor: l.valor, descricao: l.descricao, forma_pagamento: l.forma_pagamento,
      observacao: l.observacao || '',
      animal_id:  l.animal_id  ? String(l.animal_id)  : '',
      lote_id:    l.lote_id    ? String(l.lote_id)    : '',
      produto_id: l.produto_id ? String(l.produto_id) : '',
    });
    setShowLanc(true);
  };
  const salvarLanc = async () => {
    try {
      const data = {
        ...formLanc,
        animal_id:  formLanc.animal_id  || null,
        lote_id:    formLanc.lote_id    || null,
        produto_id: formLanc.produto_id || null,
      };
      if (editLanc) await api.put(`/fluxo-caixa/lancamentos/${editLanc.id}`, data);
      else          await api.post('/fluxo-caixa/lancamentos', data);
      toast.success(editLanc ? 'Lançamento atualizado!' : 'Lançamento registrado!');
      setShowLanc(false);
      reloadLancamentos();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao salvar lançamento'); }
  };
  const excluirLanc = async (id, desc) => {
    if (!window.confirm(`Excluir lançamento "${desc}"?`)) return;
    try {
      await api.delete(`/fluxo-caixa/lancamentos/${id}`);
      toast.success('Lançamento excluído');
      setLancs(p => p.filter(l => l.id !== id));
      setTotalLanc(p => p - 1);
      const rp = { data_inicio: filtros.data_inicio, data_fim: filtros.data_fim };
      if (filtros.conta_id) rp.conta_id = filtros.conta_id;
      api.get('/fluxo-caixa/resumo', { params: rp }).then(r => setResumo(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  // ── CRUD Categorias ──
  const abrirNovaCat  = () => { setEditCat(null); setFormCat(CAT_VAZIA); setShowCat(true); };
  const abrirEditCat  = c  => { setEditCat(c); setFormCat({ nome: c.nome, tipo: c.tipo, grupo: c.grupo || '', ativo: !!c.ativo }); setShowCat(true); };
  const salvarCat = async () => {
    try {
      if (editCat) await api.put(`/fluxo-caixa/categorias/${editCat.id}`, formCat);
      else         await api.post('/fluxo-caixa/categorias', formCat);
      toast.success(editCat ? 'Categoria atualizada!' : 'Categoria criada!');
      setShowCat(false); reloadCats();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao salvar categoria'); }
  };
  const excluirCat = async (id, nome) => {
    if (!window.confirm(`Excluir/desativar "${nome}"?`)) return;
    try {
      const r = await api.delete(`/fluxo-caixa/categorias/${id}`);
      toast.success(r.data?.message || 'Categoria removida');
      reloadCats();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  // ── CRUD Contas ──
  const abrirNovaConta  = () => { setEditConta(null); setFormConta(CONTA_VAZIA); setShowConta(true); };
  const abrirEditConta  = c  => {
    setEditConta(c);
    setFormConta({ nome: c.nome, tipo: c.tipo, saldo_inicial: c.saldo_inicial, data_saldo_inicial: c.data_saldo_inicial?.slice(0, 10) || hoje(), ativo: !!c.ativo });
    setShowConta(true);
  };
  const salvarConta = async () => {
    try {
      if (editConta) await api.put(`/fluxo-caixa/contas/${editConta.id}`, formConta);
      else           await api.post('/fluxo-caixa/contas', formConta);
      toast.success(editConta ? 'Conta atualizada!' : 'Conta criada!');
      setShowConta(false); reloadContas();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao salvar conta'); }
  };
  const excluirConta = async (id, nome) => {
    if (!window.confirm(`Excluir/desativar "${nome}"?`)) return;
    try {
      const r = await api.delete(`/fluxo-caixa/contas/${id}`);
      toast.success(r.data?.message || 'Conta removida');
      reloadContas();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  // ── Exportações ──
  const exportarCSV = () => {
    const token = localStorage.getItem('token');
    const q = new URLSearchParams();
    ['data_inicio','data_fim','tipo','status','conta_id','categoria_id'].forEach(k => filtros[k] && q.append(k, filtros[k]));
    fetch(`/api/fluxo-caixa/exportar/csv?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'fluxo_caixa.csv'; a.click(); URL.revokeObjectURL(a.href); toast.success('CSV exportado!'); })
      .catch(() => toast.error('Erro ao exportar CSV'));
  };
  const exportarPDF = () => {
    const token = localStorage.getItem('token');
    const q = new URLSearchParams();
    ['data_inicio','data_fim','conta_id'].forEach(k => filtros[k] && q.append(k, filtros[k]));
    fetch(`/api/relatorios/pdf/fluxo-caixa?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'fluxo_caixa.pdf'; a.click(); URL.revokeObjectURL(a.href); toast.success('PDF gerado!'); })
      .catch(() => toast.error('Erro ao gerar PDF'));
  };

  // ─── render ───────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Carregando Fluxo de Caixa...</div>;

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Fluxo de Caixa</h1>
          <p>Registro manual de entradas e saídas financeiras da propriedade</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNovoLanc}><Plus size={16} /> Novo Lançamento</button>
      </div>

      {/* Barra de período / conta */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Filter size={14} color="var(--text-secondary)" />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Período:</span>
          <input type="date" value={filtros.data_inicio} onChange={e => { fltF('data_inicio', e.target.value); setPagina(1); }}
            style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }} />
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>até</span>
          <input type="date" value={filtros.data_fim} onChange={e => { fltF('data_fim', e.target.value); setPagina(1); }}
            style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }} />
          <select value={filtros.conta_id} onChange={e => { fltF('conta_id', e.target.value); setPagina(1); }}
            style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }}>
            <option value="">Todas as contas</option>
            {contas.filter(c => c.ativo).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={exportarCSV}><Download size={13} /> CSV</button>
            <button className="btn btn-secondary btn-sm" onClick={exportarPDF}><Download size={13} /> PDF</button>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'visao_geral', label: 'Visão Geral' },
          { key: 'lancamentos', label: `Lançamentos${totalLanc ? ` (${totalLanc})` : ''}` },
          { key: 'categorias',  label: 'Categorias' },
          { key: 'caixas',      label: 'Caixas/Contas' },
          { key: 'relatorios',  label: 'Relatórios' },
        ].map(t => (
          <button key={t.key} className={`btn ${tab === t.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ VISÃO GERAL ═══ */}
      {tab === 'visao_geral' && (
        <div>
          {/* Cards */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            {[
              { label: 'Saldo Atual',          value: fmtBRL(resumo?.saldo_atual),           color: (resumo?.saldo_atual ?? 0) >= 0 ? '#2D6A4F' : '#DC2626', bg: '#DCFCE7', sub: 'acumulado · somente realizado' },
              { label: 'Entradas do Período',  value: fmtBRL(resumo?.entradas_realizadas),   color: '#16A34A', bg: '#F0FDF4', sub: 'realizadas' },
              { label: 'Saídas do Período',    value: fmtBRL(resumo?.saidas_realizadas),     color: '#DC2626', bg: '#FEF2F2', sub: 'realizadas' },
              { label: 'Resultado',            value: fmtBRL(resumo?.resultado_realizado),   color: (resumo?.resultado_realizado ?? 0) >= 0 ? '#2563EB' : '#DC2626', bg: '#EFF6FF', sub: 'entradas − saídas' },
              { label: 'Saldo Projetado',      value: fmtBRL(resumo?.saldo_projetado),       color: '#7C3AED', bg: '#EDE9FE', sub: 'incl. previstos' },
              { label: 'Maior Receita',        value: resumo?.maior_categoria_entrada?.categoria || '—', color: '#0891B2', bg: '#CFFAFE', sub: resumo?.maior_categoria_entrada ? fmtBRL(resumo.maior_categoria_entrada.total) : 'sem entradas' },
              { label: 'Maior Despesa',        value: resumo?.maior_categoria_saida?.categoria  || '—', color: '#EA580C', bg: '#FFEDD5', sub: resumo?.maior_categoria_saida  ? fmtBRL(resumo.maior_categoria_saida.total)  : 'sem saídas' },
            ].map(c => (
              <div key={c.label} className="stat-card" style={{ borderLeft: `4px solid ${c.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="stat-label">{c.label}</div>
                    <div className="stat-value" style={{ color: c.color, fontSize: c.label.startsWith('Maior') ? 15 : undefined, whiteSpace: c.label.startsWith('Maior') ? undefined : 'nowrap' }}>{c.value}</div>
                    <div className="stat-sub" style={{ whiteSpace: c.label.startsWith('Maior') ? 'nowrap' : undefined }}>{c.sub}</div>
                  </div>
                  <div style={{ padding: 10, borderRadius: 10, background: c.bg, flexShrink: 0 }}><Wallet size={18} color={c.color} /></div>
                </div>
              </div>
            ))}
          </div>

          {/* Resultado Operacional — separa receita/custo da atividade de aportes, financiamentos, investimentos e compra de animais */}
          {resumo && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Resultado Operacional</h3>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Desempenho da atividade de ovinocultura no período, sem aportes, financiamentos, investimentos ou compra de animais
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {[
                  { label: 'Entradas Operacionais', value: fmtBRL(resumo.entradas_operacionais), color: '#16A34A' },
                  { label: 'Saídas Operacionais',    value: fmtBRL(resumo.saidas_operacionais),   color: '#DC2626' },
                  { label: 'Resultado Operacional',  value: fmtBRL(resumo.resultado_operacional), color: resumo.resultado_operacional >= 0 ? '#2563EB' : '#DC2626' },
                ].map(c => (
                  <div key={c.label} className="stat-card" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0, minWidth: 0 }}>
                    <div className="stat-label">{c.label}</div>
                    <div className="stat-value" style={{ color: c.color, whiteSpace: 'nowrap' }}>{c.value}</div>
                  </div>
                ))}
              </div>
              {(resumo.entradas_nao_operacionais > 0 || resumo.saidas_nao_operacionais > 0) && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
                  Fora do resultado operacional no período: {fmtBRL(resumo.entradas_nao_operacionais)} em entradas
                  (aportes/financiamentos) e {fmtBRL(resumo.saidas_nao_operacionais)} em saídas
                  (investimentos/compra de animais) — detalhado nos gráficos por grupo abaixo.
                </p>
              )}
            </div>
          )}

          {/* Gráficos */}
          {graficos && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Entradas × Saídas por mês */}
              <div className="card">
                <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Entradas × Saídas por Mês</h3>
                {(graficos.fluxo_mensal?.length ?? 0) > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={graficos.fluxo_mensal}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmtBRL(v)} />
                      <Legend />
                      <Bar dataKey="entradas" name="Entradas" fill="#16A34A" />
                      <Bar dataKey="saidas"   name="Saídas"   fill="#DC2626" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="empty-state" style={{ height: 200 }}><p>Sem dados no período</p></div>}
              </div>

              {/* Saldo acumulado */}
              <div className="card">
                <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Saldo Acumulado</h3>
                {(graficos.fluxo_diario?.length ?? 0) > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={graficos.fluxo_diario}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="data" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmtBRL(v)} />
                      <Line type="monotone" dataKey="saldo_acumulado" name="Saldo" stroke="#2D6A4F" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="empty-state" style={{ height: 200 }}><p>Sem dados no período</p></div>}
              </div>

              {/* Saídas por categoria */}
              <div className="card">
                <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Saídas por Categoria</h3>
                {(graficos.categorias_saida?.length ?? 0) > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={graficos.categorias_saida} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="categoria" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip formatter={v => fmtBRL(v)} />
                      <Bar dataKey="total" name="Valor" fill="#DC2626" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="empty-state" style={{ height: 200 }}><p>Sem saídas no período</p></div>}
              </div>

              {/* Entradas por categoria */}
              <div className="card">
                <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Entradas por Categoria</h3>
                {(graficos.categorias_entrada?.length ?? 0) > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={graficos.categorias_entrada} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="categoria" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip formatter={v => fmtBRL(v)} />
                      <Bar dataKey="total" name="Valor" fill="#16A34A" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="empty-state" style={{ height: 200 }}><p>Sem entradas no período</p></div>}
              </div>

              {/* Saídas por Grupo Gerencial */}
              <div className="card">
                <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Saídas por Grupo Gerencial</h3>
                {(graficos.grupos_saida?.length ?? 0) > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={graficos.grupos_saida} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="grupo" tick={{ fontSize: 11 }} width={160} />
                      <Tooltip formatter={v => fmtBRL(v)} />
                      <Bar dataKey="total" name="Valor" fill="#B91C1C" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="empty-state" style={{ height: 200 }}><p>Sem saídas no período</p></div>}
              </div>

              {/* Entradas por Grupo Gerencial */}
              <div className="card">
                <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Entradas por Grupo Gerencial</h3>
                {(graficos.grupos_entrada?.length ?? 0) > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={graficos.grupos_entrada} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="grupo" tick={{ fontSize: 11 }} width={160} />
                      <Tooltip formatter={v => fmtBRL(v)} />
                      <Bar dataKey="total" name="Valor" fill="#15803D" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="empty-state" style={{ height: 200 }}><p>Sem entradas no período</p></div>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ LANÇAMENTOS ═══ */}
      {tab === 'lancamentos' && (
        <div>
          <div className="card" style={{ marginBottom: 12, padding: '12px 16px' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={filtros.tipo} onChange={e => { fltF('tipo', e.target.value); setPagina(1); }}
                style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }}>
                <option value="">Tipo: Todos</option>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </select>
              <select value={filtros.status} onChange={e => { fltF('status', e.target.value); setPagina(1); }}
                style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }}>
                <option value="">Status: Todos</option>
                <option value="realizado">Realizado</option>
                <option value="previsto">Previsto</option>
              </select>
              <input type="text" placeholder="Buscar descrição..." value={filtros.busca}
                onChange={e => { fltF('busca', e.target.value); setPagina(1); }}
                style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, minWidth: 180 }} />
              {(filtros.tipo || filtros.status || filtros.busca) && (
                <button className="btn btn-secondary btn-sm" onClick={() => { setFiltros(p => ({ ...p, tipo: '', status: '', busca: '' })); setPagina(1); }}>
                  <X size={13} /> Limpar
                </button>
              )}
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th>Data</th><th>Tipo</th><th>Status</th><th>Conta</th>
                  <th>Categoria</th><th>Descrição</th>
                  <th style={{ textAlign: 'right' }}>Valor</th><th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loadingL ? (
                  <tr><td colSpan={8}><div className="empty-state"><p>Carregando...</p></div></td></tr>
                ) : lancamentos.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><p>Nenhum lançamento encontrado</p></div></td></tr>
                ) : lancamentos.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontSize: 13 }}>{fmtDate(l.data_movimento)}</td>
                    <td>
                      <span className={`badge ${l.tipo === 'entrada' ? 'badge-green' : 'badge-red'}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {l.tipo === 'entrada' ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {l.tipo}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${l.status === 'realizado' ? 'badge-blue' : 'badge-yellow'}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {l.status === 'realizado' ? <CheckCircle size={11} /> : <Clock size={11} />} {l.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{l.conta_nome || '—'}</td>
                    <td style={{ fontSize: 13 }}>{l.categoria_nome || '—'}</td>
                    <td style={{ fontSize: 13, maxWidth: 200 }}>{l.descricao}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: l.tipo === 'entrada' ? '#16A34A' : '#DC2626' }}>
                      {fmtBRL(l.valor)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => abrirEditLanc(l)}><Edit2 size={13} /></button>
                        <button className="btn btn-secondary btn-sm" style={{ color: '#DC2626' }} onClick={() => excluirLanc(l.id, l.descricao)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPags > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}><ChevronLeft size={14} /></button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Página {pagina} de {totalPags} · {totalLanc} registros</span>
              <button className="btn btn-secondary btn-sm" disabled={pagina >= totalPags} onClick={() => setPagina(p => p + 1)}><ChevronRight size={14} /></button>
            </div>
          )}
        </div>
      )}

      {/* ═══ CATEGORIAS ═══ */}
      {tab === 'categorias' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={abrirNovaCat}><Plus size={16} /> Nova Categoria</button>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead><tr><th>Nome</th><th>Tipo</th><th>Grupo</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {todasCats.length === 0 ? (
                  <tr><td colSpan={5}><div className="empty-state"><p>Nenhuma categoria cadastrada</p></div></td></tr>
                ) : todasCats.map(c => (
                  <tr key={c.id} style={{ opacity: c.ativo ? 1 : 0.5 }}>
                    <td style={{ fontWeight: 600 }}>{c.nome}</td>
                    <td><span className={`badge ${c.tipo === 'entrada' ? 'badge-green' : c.tipo === 'saida' ? 'badge-red' : 'badge-blue'}`}>{c.tipo}</span></td>
                    <td style={{ fontSize: 13 }}>{c.grupo || '—'}</td>
                    <td><span className={`badge ${c.ativo ? 'badge-green' : 'badge-gray'}`}>{c.ativo ? 'Ativa' : 'Inativa'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => abrirEditCat(c)}>Editar</button>
                        <button className="btn btn-secondary btn-sm" style={{ color: '#DC2626' }} onClick={() => excluirCat(c.id, c.nome)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ CAIXAS/CONTAS ═══ */}
      {tab === 'caixas' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={abrirNovaConta}><Plus size={16} /> Nova Conta</button>
          </div>
          {contas.length === 0 ? (
            <div className="card"><div className="empty-state"><p>Nenhuma conta cadastrada</p></div></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {contas.map(c => (
                <div key={c.id} className="card"
                  style={{ borderLeft: `4px solid ${c.ativo ? 'var(--color-primary)' : 'var(--border-color)'}`, opacity: c.ativo ? 1 : 0.6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{c.nome}</div>
                      <span className="badge badge-gray" style={{ fontSize: 11, marginTop: 4 }}>{c.tipo}</span>
                      {!c.ativo && <span className="badge badge-gray" style={{ fontSize: 11, marginLeft: 4 }}>inativa</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => abrirEditConta(c)}>Editar</button>
                      <button className="btn btn-secondary btn-sm" style={{ color: '#DC2626' }} onClick={() => excluirConta(c.id, c.nome)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Saldo inicial</div>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>{fmtBRL(c.saldo_inicial)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>em {fmtDate(c.data_saldo_inicial)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ RELATÓRIOS ═══ */}
      {tab === 'relatorios' && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>Exportar</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Período: {fmtDate(filtros.data_inicio)} a {fmtDate(filtros.data_fim)}
              {filtros.conta_id ? ` · ${contas.find(c => String(c.id) === filtros.conta_id)?.nome}` : ' · Todas as contas'}
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={exportarCSV}><Download size={16} /> Exportar CSV</button>
              <button className="btn btn-secondary" onClick={exportarPDF}><Download size={16} /> Gerar PDF</button>
            </div>
          </div>

          {resumo && (
            <div className="card">
              <h3 style={{ marginBottom: 16, fontSize: 15, fontWeight: 600 }}>Resumo do Período</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 0 }}>
                {[
                  { label: 'Saldo Atual (acumulado)',  value: fmtBRL(resumo.saldo_atual),            color: '#2D6A4F' },
                  { label: 'Entradas Realizadas',       value: fmtBRL(resumo.entradas_realizadas),   color: '#16A34A' },
                  { label: 'Saídas Realizadas',         value: fmtBRL(resumo.saidas_realizadas),     color: '#DC2626' },
                  { label: 'Resultado Realizado',       value: fmtBRL(resumo.resultado_realizado),   color: resumo.resultado_realizado >= 0 ? '#2563EB' : '#DC2626' },
                  { label: 'Entradas Previstas',        value: fmtBRL(resumo.entradas_previstas),    color: '#16A34A' },
                  { label: 'Saídas Previstas',          value: fmtBRL(resumo.saidas_previstas),      color: '#DC2626' },
                  { label: 'Saldo Projetado',           value: fmtBRL(resumo.saldo_projetado),       color: '#7C3AED' },
                ].map(item => (
                  <div key={item.label} style={{ padding: '14px 12px', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: item.color, marginTop: 4 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* MODAL LANÇAMENTO                            */}
      {/* ═══════════════════════════════════════════ */}
      {showLanc && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowLanc(false)}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>{editLanc ? 'Editar Lançamento' : 'Novo Lançamento'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowLanc(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Data *</label>
                  <input type="date" value={formLanc.data_movimento} onChange={e => lancF('data_movimento', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Tipo *</label>
                  <select value={formLanc.tipo} onChange={e => { lancF('tipo', e.target.value); lancF('categoria_id', ''); }}>
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saída</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select value={formLanc.status} onChange={e => lancF('status', e.target.value)}>
                    <option value="realizado">Realizado</option>
                    <option value="previsto">Previsto</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Conta *</label>
                  <select value={formLanc.conta_id} onChange={e => lancF('conta_id', e.target.value)}>
                    <option value="">Selecione</option>
                    {contas.filter(c => c.ativo).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Categoria *</label>
                  <select value={formLanc.categoria_id} onChange={e => lancF('categoria_id', e.target.value)}>
                    <option value="">Selecione ({formLanc.tipo})</option>
                    {catsFiltradas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Valor (R$) *</label>
                  <input type="number" step="0.01" min="0.01" value={formLanc.valor}
                    onChange={e => lancF('valor', e.target.value)} placeholder="0,00" />
                </div>
                <div className="form-group">
                  <label>Forma de Pagamento</label>
                  <select value={formLanc.forma_pagamento} onChange={e => lancF('forma_pagamento', e.target.value)}>
                    {FORMAS_PAG.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Descrição *</label>
                <input value={formLanc.descricao} onChange={e => lancF('descricao', e.target.value)}
                  placeholder="Ex: Compra de ração – Fornecedor X" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Animal (opcional)</label>
                  <AnimalSearchSelect value={formLanc.animal_id} onChange={id => lancF('animal_id', id)} emptyLabel="Sem animal" />
                </div>
                <div className="form-group">
                  <label>Lote (opcional)</label>
                  <select value={formLanc.lote_id} onChange={e => lancF('lote_id', e.target.value)}>
                    <option value="">—</option>
                    {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Produto/Insumo (opcional)</label>
                <select value={formLanc.produto_id} onChange={e => lancF('produto_id', e.target.value)}>
                  <option value="">—</option>
                  {produtos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Observação</label>
                <textarea rows={2} value={formLanc.observacao} onChange={e => lancF('observacao', e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowLanc(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarLanc}
                disabled={!formLanc.conta_id || !formLanc.categoria_id || !formLanc.valor || !formLanc.descricao}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* MODAL CATEGORIA                             */}
      {/* ═══════════════════════════════════════════ */}
      {showCat && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCat(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editCat ? 'Editar Categoria' : 'Nova Categoria'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCat(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label>Nome *</label><input value={formCat.nome} onChange={e => catF('nome', e.target.value)} /></div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo *</label>
                  <select value={formCat.tipo} onChange={e => catF('tipo', e.target.value)}>
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saída</option>
                    <option value="ambos">Ambos</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Grupo Gerencial</label>
                  <select value={formCat.grupo} onChange={e => catF('grupo', e.target.value)}>
                    <option value="">— Sem grupo —</option>
                    {gruposDisponiveis.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              {editCat && (
                <div className="form-group">
                  <label>Status</label>
                  <select value={formCat.ativo ? '1' : '0'} onChange={e => catF('ativo', e.target.value === '1')}>
                    <option value="1">Ativa</option>
                    <option value="0">Inativa</option>
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCat(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarCat} disabled={!formCat.nome}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* MODAL CONTA                                 */}
      {/* ═══════════════════════════════════════════ */}
      {showConta && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowConta(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editConta ? 'Editar Conta' : 'Nova Conta/Caixa'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowConta(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome *</label>
                  <input value={formConta.nome} onChange={e => ctaF('nome', e.target.value)} placeholder="Ex: Caixa Principal" />
                </div>
                <div className="form-group">
                  <label>Tipo *</label>
                  <select value={formConta.tipo} onChange={e => ctaF('tipo', e.target.value)}>
                    {TIPOS_CONTA.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Saldo Inicial (R$)</label>
                  <input type="number" step="0.01" value={formConta.saldo_inicial} onChange={e => ctaF('saldo_inicial', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Data do Saldo Inicial *</label>
                  <input type="date" value={formConta.data_saldo_inicial} onChange={e => ctaF('data_saldo_inicial', e.target.value)} />
                </div>
              </div>
              {editConta && (
                <div className="form-group">
                  <label>Status</label>
                  <select value={formConta.ativo ? '1' : '0'} onChange={e => ctaF('ativo', e.target.value === '1')}>
                    <option value="1">Ativa</option>
                    <option value="0">Inativa</option>
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowConta(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarConta} disabled={!formConta.nome || !formConta.data_saldo_inicial}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
